import { PersonSearch, RocketLaunch } from '@mui/icons-material';
import { Box, Chip, LinearProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { StatusOption } from '@sharedInterfaces/ICompany';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import LandingPage from '@src/App/Components/Core/Layouts/LandingPage/LandingPage';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import type { AppState } from '@store/store';
import { ClipboardCopy, ClipboardList, ClipboardPlus, Sparkles, User, Users } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import MvpOpportunityDialog from './MvpOpportunityDialog';

/**
 * Landing Page für Opportunities - Chancen ergreifen mit KI-Power
 */
function LandingMvpOpportunity() {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).opportunities;
    const opportunities = useSelector((state: AppState) => state.opportunity);
    const statusOptions = useSelector((state: AppState) => state.company.opportunitySettings.statusOptions);
    const statusOptionsMap = new Map<string, StatusOption>(statusOptions.map((option) => [option.id, option]));

    const [dialogOpen, setDialogOpen] = useState(false);

    // Get latest 3 opportunities for widget
    const latestOpportunities = opportunities?.slice(-3) || [];

    const processSteps = [
        {
            id: 'receive',
            title: 'Anfrage erhalten',
            description: 'Projektanfragen aus verschiedenen Quellen sammeln und zentral verwalten',
            icon: <ClipboardCopy size={48} />,
        },
        {
            id: 'analyze',
            title: 'KI-Analyse',
            description:
                'Automatische Extraktion von Skills, Zeitrahmen und Anforderungen durch intelligente Textanalyse',
            icon: <Sparkles size={48} />,
        },
        {
            id: 'match',
            title: 'Mitarbeiter-Matching',
            description: 'Perfekte Kandidaten finden basierend auf Skills, Verfügbarkeit und Projekterfahrung',
            icon: <PersonSearch />,
        },
        {
            id: 'start',
            title: 'Projekt starten',
            description: 'Nahtloser Übergang vom Matching zur Projektrealisierung mit optimaler Teamzusammenstellung',
            icon: <RocketLaunch />,
        },
    ];

    const quickLinks = [
        {
            id: 'create',
            title: 'Neue Anfrage erstellen',
            description: 'Projektanfrage mit KI-Unterstützung schnell und präzise erfassen',
            icon: <ClipboardPlus size={28} />,
            variant: 'primary' as const,
            onClick: () => setDialogOpen(true),
        },
        {
            id: 'overview',
            title: 'Alle Anfragen anzeigen',
            description: 'Vollständige Übersicht aller Projektanfragen mit erweiterten Filtermöglichkeiten',
            icon: <ClipboardList size={28} />,
            variant: 'secondary' as const,
            onClick: () => navigate('/sales/opportunities/list'),
        },
    ];

    const [activeRole, setActiveRole] = useState('employee');

    const roles: IMvpRole[] = [
        {
            id: 'employee',
            label: 'Mitarbeiter',
            icon: <User size={18} />,
        },
        {
            id: 'admin',
            label: 'Admin',
            icon: <Users size={18} />,
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'create',
            label: 'Anfrage erstellen',
            icon: <ClipboardPlus size={18} />,
            onClick: () => setDialogOpen(true),
            variant: 'primary',
        },
        {
            id: 'overview',
            label: 'Übersicht',
            icon: <ClipboardList size={18} />,
            onClick: () => navigate('/sales/opportunities/list'),
        },
    ];

    const renderRecentOpportunitiesWidget = () => (
        <Stack spacing={2}>
            {latestOpportunities.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    Noch keine Anfragen vorhanden
                </Typography>
            ) : (
                latestOpportunities.map((opportunity) => {
                    const statusOption = statusOptionsMap.get(opportunity.status);
                    const skillCoverage = opportunity.selectedEmployees.length
                        ? Math.round(
                              (opportunity.selectedEmployees.reduce((prev, cur) => cur.skillCoverage + prev, 0) /
                                  opportunity.selectedEmployees.length) *
                                  100,
                          )
                        : 0;

                    return (
                        <Paper
                            key={opportunity.id}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    borderColor: theme.palette.primary.main,
                                    transform: 'translateY(-2px)',
                                },
                            }}
                            onClick={() => navigate(`/${ELinks.SALES_OPPORTUNITIES}/${opportunity.id}`)}
                        >
                            <Stack spacing={1}>
                                <Box
                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                        {opportunity.title || 'Unbenannte Anfrage'}
                                    </Typography>
                                    <Chip
                                        label={statusOption?.title || 'Unbekannt'}
                                        color={statusOption?.isActive ? 'success' : 'default'}
                                        size="small"
                                        variant="outlined"
                                    />
                                </Box>

                                {opportunity.customer && (
                                    <Typography variant="caption" color="text.secondary">
                                        {opportunity.customer}
                                    </Typography>
                                )}

                                {skillCoverage > 0 && (
                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Skill-Abdeckung
                                            </Typography>
                                            <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                {skillCoverage}%
                                            </Typography>
                                        </Box>
                                        <LinearProgress
                                            variant="determinate"
                                            value={skillCoverage}
                                            sx={{
                                                height: 4,
                                                borderRadius: 2,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                '& .MuiLinearProgress-bar': {
                                                    bgcolor:
                                                        skillCoverage >= 80
                                                            ? theme.palette.success.main
                                                            : skillCoverage >= 60
                                                              ? theme.palette.warning.main
                                                              : theme.palette.error.main,
                                                },
                                            }}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    );
                })
            )}
        </Stack>
    );

    return (
        <>
            <MvpSubNav
                currentPageTitle="Anfragen"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />

            <LandingPage
                title="Chancen ergreifen"
                subtitle="KI-gestützte Anfrage-Analyse & intelligentes Mitarbeiter-Matching"
                description="Intelligente Projektanfragen-Verwaltung: KI analysiert Anforderungen, extrahiert Skills und findet passende Teammitglieder automatisch."
                heroIcon={
                    <Box
                        component="img"
                        src="/t4loOpportunitiesIllustration.png"
                        alt="Opportunities Illustration"
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            boxShadow: 'none',
                            filter: 'none',
                        }}
                    />
                }
                processTitle="Vom Eingang zum Erfolg"
                steps={processSteps}
                quickLinksTitle="Schnellzugriff"
                quickLinks={quickLinks}
                widgets={[
                    {
                        id: 'recent-opportunities',
                        title: 'Aktuelle Anfragen',
                        content: renderRecentOpportunitiesWidget(),
                    },
                ]}
            />

            <MvpOpportunityDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={() => setDialogOpen(false)}
            />
        </>
    );
}

export default LandingMvpOpportunity;
